import React, { useState, useMemo, useEffect } from 'react';
import Calendar from 'react-calendar';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Banknote } from 'lucide-react';
import axios from 'axios';

function SettlementCalendar() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  // 달력에서 현재 보고 있는 월 (네비 < > 로 바꿀 때마다 변경)
  const [activeStartDate, setActiveStartDate] = useState(new Date());
  const [calendarRows, setCalendarRows] = useState([]);

  const activeMonthKey = useMemo(
    () => dayjs(activeStartDate).format('YYYY-MM'),
    [activeStartDate]
  );

  useEffect(() => {
    const fetchCalendarSummary = async () => {
      const baseUrl = process.env.REACT_APP_API || '';
      try {
        const { data } = await axios.get(
          baseUrl + '/api/cp/summary/calendar',
          {
            params: { month: activeMonthKey },
            withCredentials: true,
          }
        );

        let rows = [];
        if (data?.code === 1000 && Array.isArray(data?.data?.rows)) {
          rows = data.data.rows;
        }
        setCalendarRows(rows);
      } catch {
        setCalendarRows([]);
      }
    };

    fetchCalendarSummary();
  }, [activeMonthKey]);

  // 날짜별 요약 맵: { [date]: { paymentAmount, cancelAmount, settlementAmount, ... } }
  const summaryMap = useMemo(() => {
    const map = {};
    calendarRows.forEach((row) => {
      if (!row?.date) return;
      map[row.date] = row;
    });
    return map;
  }, [calendarRows]);

  // 현재 달력에서 보고 있는 월(activeStartDate) 기준 매출(승인-취소)/정산 합계
  const monthlyStats = useMemo(() => {
    const y = dayjs(activeStartDate).year();
    const m = dayjs(activeStartDate).month();
    let revenue = 0;
    let settlement = 0;
    calendarRows.forEach((row) => {
      if (!row?.date) return;
      const d = dayjs(row.date);
      if (d.year() === y && d.month() === m) {
        const paymentAmount = Number(row.paymentAmount || 0);
        const cancelAmount = Number(row.cancelAmount || 0);
        revenue += paymentAmount - cancelAmount;
        settlement += Number(row.settlementAmount || 0);
      }
    });
    return { revenue, settlement };
  }, [activeStartDate, calendarRows]);

  return (
    <div className="flex-1 min-h-0 py-5 px-8 overflow-auto space-y-6">
      <div className="grid grid-cols-[1.3fr,0.7fr] gap-6 items-stretch">
        {/* 달력 */}
        <div className="bg-white rounded-2xl border border-[#e0e0e0] shadow-md p-4">
          {/* 해당 월 전체 승인/정산 통계 (월 네비 위) */}
          <div className="flex items-center justify-between gap-4 mb-4 pb-4 border-b border-[#e5e8eb]">
            <span className="text-[15px] font-bold text-[#191f28]">
              {dayjs(activeStartDate).format('YYYY년 M월')} 통계
            </span>
            <div className="flex items-center gap-6 text-[14px] text-[#4e5968]">
              <span className="flex items-center gap-1">
                <span>매출</span>
                <strong className="text-[#191f28]">{monthlyStats.revenue.toLocaleString()}원</strong>
              </span>
              <span className="flex items-center gap-1">
                <span>정산금액</span>
                <strong className="text-[#0064ff]">{monthlyStats.settlement.toLocaleString()}원</strong>
              </span>
            </div>
          </div>
          <Calendar
            locale="ko-KR"
            value={selectedDate}
            onChange={setSelectedDate}
            activeStartDate={activeStartDate}
            onActiveStartDateChange={({ activeStartDate: next }) => setActiveStartDate(next)}
            formatDay={(_, date) => dayjs(date).date().toString()}
            tileContent={({ date, view }) => {
              if (view !== 'month') return null;
              const key = dayjs(date).format('YYYY-MM-DD');
              const row = summaryMap[key];
              if (!row) return null;
              const paymentAmount = Number(row.paymentAmount || 0);
              const cancelAmount = Number(row.cancelAmount || 0);
              const revenueAmount = paymentAmount - cancelAmount;
              const settlementAmount = Number(row.settlementAmount || 0);
              if (!revenueAmount && !settlementAmount) return null;
              return (
                <div className="mt-1 w-full text-[12px] text-[#4e5968]">
                  {revenueAmount !== 0 && (
                    <div className="text-right">
                      <div className="text-[13px] text-[#8b95a1]">매출</div>
                      <div className="text-[17px] font-medium text-[#191f28]">
                        {revenueAmount.toLocaleString()}원
                      </div>
                    </div>
                  )}
                  {settlementAmount > 0 && (
                    <div className="text-right">
                      <div className="text-[13px] text-[#8b95a1]">정산</div>
                      <div className="text-[17px] font-semibold text-[#0064ff]">
                        {settlementAmount.toLocaleString()}원
                      </div>
                    </div>
                  )}
                </div>
              );
            }}
            tileClassName={({ date, view }) => {
              if (view !== 'month') return '';
              const key = dayjs(date).format('YYYY-MM-DD');
              const row = summaryMap[key];
              const hasSettle = row && Number(row.settlementAmount || 0) > 0;
              const isCurrentMonth =
                dayjs(date).year() === dayjs(activeStartDate).year() &&
                dayjs(date).month() === dayjs(activeStartDate).month();
              const dayOfWeek = date.getDay(); // 0:일 ~ 6:토
              const isWeekendInCurrentMonth =
                isCurrentMonth && (dayOfWeek === 0 || dayOfWeek === 6);

              let cls = '';
              if (hasSettle) cls += ' has-settlement';
              if (isWeekendInCurrentMonth) cls += ' text-red-500';
              return cls.trim();
            }}
            className="w-full border-none text-[14px] react-calendar-custom"
          />
        </div>

        {/* 선택한 날짜 정산 요약 - 달력 높이만큼 */}
        <div className="bg-white rounded-2xl border border-[#e0e0e0] shadow-md p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3 shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#f2f4f6] rounded-lg flex items-center justify-center shrink-0">
                <Banknote size={20} className="text-[#0064ff]" />
              </div>
              <div className="flex flex-col">
                <h2 className="text-[18px] font-extrabold text-[#191f28]">일별 정산 요약</h2>
                <span className="text-[13px] text-[#8b95a1]">
                  {dayjs(selectedDate).format('YYYY.MM.DD (ddd)')}
                </span>
              </div>
            </div>
          </div>

          {summaryMap[dayjs(selectedDate).format('YYYY-MM-DD')] ? (
            <div className="space-y-4">
              {(() => {
                const key = dayjs(selectedDate).format('YYYY-MM-DD');
                const row = summaryMap[key];
                const paymentAmount = Number(row?.paymentAmount || 0);
                const paymentCount = Number(row?.paymentCount || 0);
                const cancelAmount = Number(row?.cancelAmount || 0);
                const cancelCount = Number(row?.cancelCount || 0);
                const settlementAmount = Number(row?.settlementAmount || 0);
                const settlementCount = Number(row?.settlementCount || 0);
                const revenueAmount = paymentAmount - cancelAmount;
                const hasSales = paymentAmount > 0 || cancelAmount > 0 || revenueAmount !== 0;
                const hasSettlement = settlementAmount > 0;

                return (
                  <>
                    {/* 매출 영역: 승인, 취소, 매출만 (여기에 '전체내역' 버튼 배치) */}
                    {hasSales && (
                      <div className="bg-[#f9fafb] rounded-xl border border-[#e5e8eb] p-4 space-y-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <div className="text-[13px] font-bold text-[#8b95a1]">매출</div>
                          <button
                            type="button"
                            onClick={() =>
                              navigate('/cp/payments', {
                                state: {
                                  searchDate: dayjs(selectedDate).format('YYYY.MM.DD'),
                                },
                              })
                            }
                            className="text-[13px] font-bold text-[#4e5968] hover:text-[#0064ff] flex items-center gap-0.5 whitespace-nowrap"
                          >
                            전체내역<span className="text-[16px]">›</span>
                          </button>
                        </div>
                        {paymentAmount > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-[13px] text-[#8b95a1]">승인</span>
                            <span className="text-[18px] font-medium text-[#191f28]">
                              {paymentAmount.toLocaleString()}원 ({paymentCount}건)
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-[13px] text-[#8b95a1]">취소</span>
                          <span className="text-[18px] font-medium text-[#b91c1c]">
                            {cancelAmount.toLocaleString()}원 ({cancelCount}건)
                          </span>
                        </div>
                        {revenueAmount !== 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-[13px] text-[#8b95a1]">매출</span>
                            <span className="text-[18px] font-medium text-[#191f28]">
                              {revenueAmount.toLocaleString()}원
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 정산 영역: 정산만 */}
                    {hasSettlement && (
                      <div className="bg-[#f9fafb] rounded-xl border border-[#e5e8eb] p-4 space-y-3">
                        <div className="text-[13px] font-bold text-[#8b95a1] mb-2">정산</div>
                        <div className="flex items-center justify-between">
                          <span className="text-[13px] text-[#8b95a1]">정산</span>
                          <span className="text-[18px] font-bold text-[#0064ff]">
                            {settlementAmount.toLocaleString()}원 ({settlementCount}건)
                          </span>
                        </div>
                      </div>
                    )}

                    {!hasSales && !hasSettlement && (
                      <div className="bg-[#f9fafb] rounded-xl border border-[#e5e8eb] p-4 text-[13px] text-[#8b95a1]">
                        해당 날짜에 매출·정산 내역이 없습니다.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-[#adb5bd] space-y-3">
              <div className="w-12 h-12 bg-[#f2f4f6] rounded-full flex items-center justify-center shrink-0">
                <Banknote size={24} className="opacity-40" />
              </div>
              <p className="text-[14px] font-bold text-center">
                선택한 날짜에는 정산 내역이 없습니다.
              </p>
              <p className="text-[12px] text-center">
                정산이 발생한 날짜를 선택하면<br />
                일별 정산금액과 건수를 볼 수 있습니다.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SettlementCalendar;
